import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "lib/api/api";

const FINAL_SUCCESS = "SUCCEEDED";
const FINAL_FAILURES = new Set(["FAILED", "CANCELLED", "EXPIRED"]);

const PaymentReturnPage = ({ resultType = "success" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const transactionId = searchParams.get("transactionId");
  const provider = searchParams.get("provider");
  const isSuccessReturn = resultType === "success";

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(transactionId));
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!transactionId) {
      setLoading(false);
      setError(t("paymentReturn.missingTransaction"));
      return;
    }

    try {
      const response = await api.get(`/payments/${transactionId}/status`);
      setStatus(response.data);
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("paymentReturn.statusError")
      );
    } finally {
      setLoading(false);
    }
  }, [transactionId, t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!isSuccessReturn || !transactionId) {
      return undefined;
    }
    if (status?.status === FINAL_SUCCESS || FINAL_FAILURES.has(status?.status)) {
      return undefined;
    }

    const intervalId = window.setInterval(fetchStatus, 4000);
    return () => window.clearInterval(intervalId);
  }, [fetchStatus, isSuccessReturn, status?.status, transactionId]);

  const visualState = useMemo(() => {
    if (loading) {
      return "loading";
    }
    if (error) {
      return "error";
    }
    if (status?.status === FINAL_SUCCESS) {
      return "success";
    }
    if (FINAL_FAILURES.has(status?.status) || !isSuccessReturn) {
      return "failed";
    }
    return "pending";
  }, [error, isSuccessReturn, loading, status?.status]);

  const icon = {
    loading: <CircularProgress size={54} sx={{ color: "#FFC107" }} />,
    pending: <HourglassEmptyIcon sx={{ fontSize: 64, color: "#B7791F" }} />,
    success: <CheckCircleIcon sx={{ fontSize: 64, color: "#2E7D32" }} />,
    failed: <ErrorOutlineIcon sx={{ fontSize: 64, color: "#C62828" }} />,
    error: <ErrorOutlineIcon sx={{ fontSize: 64, color: "#C62828" }} />,
  }[visualState];

  const title = t(`paymentReturn.${visualState}.title`);
  const description = t(`paymentReturn.${visualState}.description`);

  return (
    <Box
      sx={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 8,
        bgcolor: "#F8FAFA",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 680,
          borderRadius: 4,
          border: "1px solid #E5E7EB",
          p: { xs: 3, md: 5 },
          textAlign: "center",
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          {icon}
          <Typography variant="h4" fontWeight={900}>
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 520 }}>
            {description}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
            {provider && (
              <Chip
                label={`${t("paymentReturn.provider")}: ${provider}`}
                sx={{ fontWeight: 700 }}
              />
            )}
            {transactionId && (
              <Chip
                label={`${t("paymentReturn.transaction")}: ${transactionId}`}
                sx={{ fontWeight: 700 }}
              />
            )}
            {status?.status && (
              <Chip
                label={`${t("paymentReturn.status")}: ${status.status}`}
                color={status.status === FINAL_SUCCESS ? "success" : "default"}
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>

          {status?.amount !== undefined && status?.amount !== null && (
            <Typography fontWeight={800}>
              {status.currency || ""} {Number(status.amount).toFixed(2)}
            </Typography>
          )}

          {visualState === "success" && status?.courseTitle && (
            <Alert severity="success" sx={{ width: "100%", textAlign: "left" }}>
              {t("paymentReturn.bookedCourse", {
                course: status.courseTitle,
                defaultValue: `Booked course: ${status.courseTitle}`,
              })}
            </Alert>
          )}

          {(error || status?.failureReason) && (
            <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>
              {error || status.failureReason}
            </Alert>
          )}

          {visualState === "pending" && (
            <Alert severity="info" sx={{ width: "100%", textAlign: "left" }}>
              {t("paymentReturn.pending.webhookNote")}
            </Alert>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ width: "100%", justifyContent: "center", pt: 1 }}
          >
            <Button
              variant="contained"
              onClick={() => navigate("/dashboard?tab=paymentHistory")}
              sx={{
                bgcolor: "#FFC107",
                color: "#111",
                fontWeight: 900,
                borderRadius: 999,
                px: 4,
                "&:hover": { bgcolor: "#E6AC00" },
              }}
            >
              {t("paymentReturn.goDashboard")}
            </Button>
            {visualState === "success" && status?.teacherCourseId && (
              <Button
                variant="contained"
                onClick={() => navigate(`/dashboard/enrolled-course/${status.teacherCourseId}`)}
                sx={{
                  bgcolor: "#111827",
                  color: "#fff",
                  fontWeight: 900,
                  borderRadius: 999,
                  px: 4,
                  "&:hover": { bgcolor: "#374151" },
                }}
              >
                {t("paymentReturn.viewBookedCourse", {
                  defaultValue: "View booked course",
                })}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={fetchStatus}
              disabled={loading || !transactionId}
              sx={{ borderRadius: 999, px: 4, fontWeight: 800 }}
            >
              {t("paymentReturn.refresh")}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default PaymentReturnPage;
